/**
 * Turn a period preset (daily / weekly / monthly / custom) plus an "anchor"
 * date into a concrete {from, to} ISO range + display label. Pure — takes the
 * race list so it can default the anchor to the latest data and clamp steps.
 *
 * daily   → the anchor day
 * weekly  → the Mon–Sun week containing the anchor
 * monthly → the calendar month containing the anchor
 * custom  → explicit from/to
 */

function bounds(races) {
  const dates = (races || []).map((r) => r.date).sort();
  return { min: dates[0] || "", max: dates[dates.length - 1] || "" };
}

export function dataBounds(races) {
  return bounds(races);
}

function addDays(iso, delta) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

function addMonths(iso, delta) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + delta);
  return d.toISOString().slice(0, 10);
}

function startOfWeek(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7; // Monday = 0
  d.setUTCDate(d.getUTCDate() - dow);
  return d.toISOString().slice(0, 10);
}

function startOfMonth(iso) {
  return `${iso.slice(0, 7)}-01`;
}

function endOfMonth(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + 1, 0); // day 0 of next month = last day
  return d.toISOString().slice(0, 10);
}

function pretty(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

function prettyDay(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

function prettyMonth(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("en-AU", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * The {from,to} ISO range for a period, computed from an explicit anchor WITHOUT
 * needing the data (so the detail page can fetch just its slice before any data
 * is loaded). `custom` needs explicit from/to; otherwise anchor drives it.
 */
export function periodRangeFor(period, anchorIso, opts = {}) {
  const anchor = anchorIso;
  switch (period) {
    case "custom":
      return { from: opts.from || "", to: opts.to || "" };
    case "daily":
      return { from: anchor, to: anchor };
    case "monthly":
      return { from: startOfMonth(anchor), to: endOfMonth(anchor) };
    case "weekly":
    default: {
      const from = startOfWeek(anchor);
      return { from, to: addDays(from, 6) };
    }
  }
}

export function resolvePeriodRange(period, races, opts = {}) {
  const { min, max } = bounds(races);
  if (!min) return { from: "", to: "", label: "", anchor: "" };

  const anchor = opts.anchor || max;

  switch (period) {
    case "custom": {
      const from = opts.from || min;
      const to = opts.to || max;
      return { from, to, label: `${pretty(from)} – ${pretty(to)}`, anchor };
    }
    case "daily":
      return { from: anchor, to: anchor, label: prettyDay(anchor), anchor };
    case "monthly": {
      const from = startOfMonth(anchor);
      const to = endOfMonth(anchor);
      return { from, to, label: prettyMonth(anchor), anchor };
    }
    case "weekly":
    default: {
      const from = startOfWeek(anchor);
      const to = addDays(from, 6);
      return { from, to, label: `${pretty(from)} – ${pretty(to)}`, anchor };
    }
  }
}

/**
 * Move the anchor by one unit of the current period, clamped to [min, max].
 * @returns {string} new anchor ISO date.
 */
export function stepAnchor(anchor, period, dir, min = "", max = "") {
  const base = anchor || max;
  if (!base) return base;
  let next = base;
  if (period === "daily") next = addDays(base, dir);
  else if (period === "weekly") next = addDays(base, 7 * dir);
  else if (period === "monthly") next = addMonths(base, dir);
  if (min && next < min) next = min;
  if (max && next > max) next = max;
  return next;
}
